import React, { useState, useEffect } from "react";

const LoadingScreen = ({ onLoadingComplete }) => {
  const [progress, setProgress] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    // 3 second loading - slower progress
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setFadeOut(true);
            setTimeout(() => {
              onLoadingComplete();
            }, 500);
          }, 200);
          return 100;
        }
        return prev + 3.5; // Slower progress for 3 seconds
      });
    }, 100);

    return () => clearInterval(interval);
  }, [onLoadingComplete]);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center transition-all duration-500 ${
        fadeOut ? "opacity-0 scale-110" : "opacity-100 scale-100"
      }`}
      style={{
        background: "linear-gradient(135deg, #0f0f10 0%, #1a1a1b 50%, #0f0f10 100%)",
      }}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating X marks */}
        <div className="absolute top-1/4 left-1/4 opacity-10">
          <svg width="120" height="120" viewBox="0 0 100 100" className="animate-spin-slow">
            <path
              d="M20,20 L80,80 M80,20 L20,80"
              stroke="#6DB432"
              strokeWidth="8"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </div>
        <div className="absolute bottom-1/4 right-1/4 opacity-10">
          <svg width="80" height="80" viewBox="0 0 100 100" className="animate-spin-reverse">
            <path
              d="M20,20 L80,80 M80,20 L20,80"
              stroke="#8BC064"
              strokeWidth="8"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </div>
        
        {/* Glowing orbs */}
        <div className="absolute top-1/3 right-1/3 w-64 h-64 bg-[#6DB432]/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/3 left-1/3 w-48 h-48 bg-[#8BC064]/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(#6DB432 1px, transparent 1px),
              linear-gradient(90deg, #6DB432 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            animation: 'gridMove 20s linear infinite'
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Logo container with glow */}
        <div className="relative mb-8">
          {/* Outer rotating ring */}
          <div className="absolute -inset-12 rounded-full border-2 border-dashed border-[#6DB432]/30 animate-spin-slow" />
          
          {/* Middle pulsing ring */}
          <div className="absolute -inset-6 rounded-full border border-[#8BC064]/50 animate-ping" style={{ animationDuration: '2s' }} />
          
          {/* Logo glow effect */}
          <div className="absolute inset-0 bg-[#6DB432]/20 rounded-full blur-3xl animate-pulse" />
          
          {/* Logo - BIGGER SIZE */}
          <div className="relative w-72 h-72 md:w-80 md:h-80 lg:w-96 lg:h-96 flex items-center justify-center">
            <img
              src="/images/logo.png"
              alt="NUVEXA Logo"
              className="w-full h-full object-contain animate-float"
            />
          </div>
        </div>

        {/* Loading bar container */}
        <div className="w-64 md:w-80 relative mt-4">
          {/* Background bar */}
          <div className="h-1.5 bg-[#353537] rounded-full overflow-hidden">
            {/* Progress bar */}
            <div
              className="h-full bg-gradient-to-r from-[#6DB432] via-[#8BC064] to-[#6DB432] rounded-full transition-all duration-300 relative"
              style={{ width: `${Math.min(progress, 100)}%` }}
            >
              {/* Shimmer effect on progress bar */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
            </div>
          </div>
          
          {/* Percentage text */}
          <div className="flex justify-between mt-3">
            <span className="text-[#7B7B7D] text-xs font-medium">Loading...</span>
            <span className="text-[#6DB432] text-xs font-bold">{Math.min(Math.round(progress), 100)}%</span>
          </div>
        </div>

        {/* Animated dots */}
        <div className="flex gap-2 mt-8">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full bg-[#6DB432] animate-bounce"
              style={{ animationDelay: `${i * 0.15}s`, animationDuration: '0.8s' }}
            />
          ))}
        </div>
      </div>

      {/* Corner decorations */}
      <div className="absolute top-8 left-8 w-16 h-16 border-t-2 border-l-2 border-[#6DB432]/30 rounded-tl-3xl" />
      <div className="absolute top-8 right-8 w-16 h-16 border-t-2 border-r-2 border-[#6DB432]/30 rounded-tr-3xl" />
      <div className="absolute bottom-8 left-8 w-16 h-16 border-b-2 border-l-2 border-[#6DB432]/30 rounded-bl-3xl" />
      <div className="absolute bottom-8 right-8 w-16 h-16 border-b-2 border-r-2 border-[#6DB432]/30 rounded-br-3xl" />
    </div>
  );
};

export default LoadingScreen;
